import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  CalendarDays,
  CheckCircle2,
  Clock,
  Gift,
  MapPin,
  Smartphone,
  Star,
  UserPlus,
  Users,
} from "lucide-react";
import { RouteMap, RouteStop } from "@/components/common/RouteMap";
import { VehicleHeroImage } from "@/components/common/VehicleHeroImage";
import { WhatsAppShareSection } from "@/components/common/WhatsAppShareSection";
import { ROUTE_PATHS } from "@/routes/routePaths";
import type { BookingDetails, BookingGuest } from "@/types/bookingDetails";
import { buildBookingShareMessage } from "@/utils/share/shareMessages";
import { openWhatsAppShare } from "@/utils/share/openWhatsAppShare";

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.hst.duriapp";
const APP_STORE_URL = "https://apps.apple.com/in/app/duri/id6755706995";

interface BookingDetailsViewProps {
  booking: BookingDetails;
}

export function BookingDetailsView({ booking }: BookingDetailsViewProps) {
  const { host, vehicle, route } = booking;

  const shareMessage = useMemo(
    () => buildBookingShareMessage(booking),
    [booking]
  );

  const rideInfoUrl = `${ROUTE_PATHS.RIDE_INFO}?rideId=${encodeURIComponent(booking.rideId)}`;

  const qrSrc =
    booking.qrCodeUrl ||
    `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(
      booking.boardingPin
    )}`;

  return (
    <div className="space-y-4 pb-12">
      {/* Success header */}
      <div className="text-center">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-600 text-white">
          <CheckCircle2 className="h-7 w-7" />
        </span>
        <h1 className="mt-4 text-2xl font-extrabold tracking-tight text-foreground">
          Your Seat is Booked!
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Hi {booking.guestName}, you're confirmed on ride {booking.rideId}.
        </p>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
          <span className="rounded-full bg-card px-3 py-1 text-xs font-semibold text-foreground shadow-sm">
            Booking #{booking.bookingId}
          </span>
        </div>
      </div>

      {/* Boarding pass */}
      <section className="overflow-hidden rounded-2xl border border-border/70 bg-card shadow-sm">
        <div className="flex items-center justify-between border-b border-dashed border-border px-5 py-3">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Boarding pass
          </span>
          <span className="text-xs font-semibold text-muted-foreground">
            Ride {booking.rideId}
          </span>
        </div>
        <div className="flex flex-col gap-4 p-5 sm:flex-row">
          <img
            src={qrSrc}
            alt="Booking QR code"
            className="h-36 w-36 shrink-0 self-center rounded-xl border border-border bg-white p-2"
          />
          <div className="flex-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Show this at pickup
            </p>
            <p className="text-sm font-bold text-foreground">
              Scan to verify your seat
            </p>
            <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3">
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-emerald-700">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Boarding PIN
              </p>
              <div className="mt-2 flex gap-2">
                {booking.boardingPin.split("").map((digit, index) => (
                  <span
                    key={`${digit}-${index}`}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-emerald-200 bg-card text-base font-bold text-emerald-700"
                  >
                    {digit}
                  </span>
                ))}
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Share with host only at boarding.
              </p>
            </div>
          </div>
        </div>
        {/* Live ride tracking — temporarily hidden
        <div className="flex items-center justify-between border-t border-border px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-600 text-white">
              <Navigation className="h-4 w-4" />
            </span>
            <div>
              <p className="text-sm font-semibold text-foreground">
                Live ride tracking
              </p>
              <p className="text-xs text-muted-foreground">
                Follow your host's location in real-time
              </p>
            </div>
          </div>
          <button
            type="button"
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
          >
            Track
          </button>
        </div>
        */}
      </section>

      {/* Ride summary */}
      <section className="rounded-2xl border border-border/70 bg-card p-5 shadow-sm">
        <p className="text-sm font-semibold text-foreground">Ride summary</p>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <SummaryTile icon={CalendarDays} label="Date" value={booking.date} />
          <SummaryTile icon={Clock} label="Time" value={booking.time} />
          <SummaryTile
            icon={Users}
            label="Seats booked"
            value={String(booking.seatsBooked)}
          />
          <SummaryTile icon={MapPin} label="Ride" value={booking.rideId} />
        </div>

        {booking.rideId && (
          <div className="mt-4 rounded-xl border border-border/70 p-4">
            <div className="flex items-center justify-between gap-3">
              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Users className="h-3.5 w-3.5" />
                Guests
              </p>
              <Link
                to={rideInfoUrl}
                className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition-colors hover:border-emerald-300 hover:bg-emerald-100"
              >
                <UserPlus className="h-3.5 w-3.5" />
                Add Guest
              </Link>
            </div>
            {booking.guests.length > 0 ? (
              <ul className="mt-3 space-y-2">
                {booking.guests.map((guest, index) => (
                  <GuestRow
                    key={`${guest.seatNumber}-${index}`}
                    guest={guest}
                  />
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-xs text-muted-foreground">
                Book another seat on this ride for a friend or family member.
              </p>
            )}
          </div>
        )}

        {/* <div className="mt-4 space-y-3 rounded-xl bg-muted/50 p-4">
          <RouteStop
            variant="start"
            label={route.pickup.label ?? "Start Location"}
            address={route.pickup.address}
          />
          <RouteStop
            variant="end"
            label={route.drop.label ?? "End Location"}
            address={route.drop.address}
          />
        </div> */}
      </section>

      {/* Route + map */}
      <section className="rounded-2xl border border-border/70 bg-card p-5 shadow-sm">
        <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
          <span className="text-emerald-600">●</span> Route
        </p>
        <div className="mt-4 space-y-4">
          <RouteStop
            variant="start"
            label={route.pickup.label ?? "Start Location"}
            address={route.pickup.address}
          />
          <RouteStop
            variant="end"
            label={route.drop.label ?? "End Location"}
            address={route.drop.address}
          />
        </div>
        <RouteMap pickup={route.pickup} drop={route.drop} className="mt-4" />
      </section>

      {/* Host */}
      <section className="flex items-center gap-3 rounded-2xl border border-border/70 bg-card p-4 shadow-sm">
        <Avatar name={host.name} src={host.avatarUrl} />
        <div>
          <p className="flex items-center gap-1 text-sm font-bold text-foreground">
            {host.name}
            {host.isVerified && (
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
            )}
          </p>
          <p className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
            Your host
            {host.rating != null && (
              <>
                {" · "}
                <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                {host.rating}
              </>
            )}
          </p>
        </div>
      </section>

      {/* Vehicle */}
      <section className="overflow-hidden rounded-2xl border border-border/70 bg-card shadow-sm">
        <VehicleHeroImage
          exteriorPhoto={vehicle.imageUrl}
          make={vehicle.make}
          model={vehicle.model}
          alt={vehicle.name}
          heightClass="h-40"
        />
        <div className="p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Vehicle
          </p>
          <p className="mt-0.5 text-base font-bold text-foreground">
            {vehicle.name}
          </p>
          {vehicle.registration && (
            <p className="mt-0.5 text-sm text-muted-foreground">
              {vehicle.registration}
            </p>
          )}
        </div>
      </section>

      {/* Pickup instructions */}
      {booking.pickupInstructions && (
        <section className="rounded-2xl border border-border/70 bg-card p-5 shadow-sm">
          <p className="text-sm font-semibold text-foreground">
            Pickup instructions
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {booking.pickupInstructions}
          </p>
        </section>
      )}

      <WhatsAppShareSection
        message={shareMessage}
        description="Share this booking with friends. Pre-filled message ready."
      />

      {/* Invite friends */}
      <section className="flex items-start gap-3 rounded-2xl border border-border/70 bg-card p-5 shadow-sm">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
          <Gift className="h-4 w-4" />
        </span>
        <div>
          <p className="text-sm font-semibold text-foreground">
            Invite friends &amp; earn free rides
          </p>
          <p className="text-xs text-muted-foreground">
            Every friend who joins Duri gives you ride credits. Travel smarter
            together.
          </p>
        </div>
      </section>

      {/* Download app */}
      <section className="rounded-2xl border border-border/70 bg-card p-5 shadow-sm">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
            <Smartphone className="h-4 w-4" />
          </span>
          <div>
            <p className="text-sm font-semibold text-foreground">
              Download Duri App &amp; save more
            </p>
            <p className="text-xs text-muted-foreground">
              Real-time tracking, exclusive ride deals.
            </p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <StoreButton href={PLAY_STORE_URL}>▶ Play Store</StoreButton>
          <StoreButton href={APP_STORE_URL}>App Store</StoreButton>
        </div>
        <button
          type="button"
          onClick={() => openWhatsAppShare(shareMessage)}
          className="mt-4 flex w-full items-center justify-center gap-1.5 text-sm font-semibold text-emerald-700 hover:underline"
        >
          <Gift className="h-4 w-4" />
          Share with friends &amp; earn rewards
        </button>
      </section>

      <p className="text-center text-xs text-muted-foreground">
        Track your ride in real-time on the Duri App.
      </p>
    </div>
  );
}

function GuestRow({ guest }: { guest: BookingGuest }) {
  const genderLabel = formatGuestGender(guest.gender);

  return (
    <li className="flex items-center justify-between gap-3 rounded-lg bg-muted/50 px-3 py-2.5">
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-foreground">
          {guest.name}
        </p>
        {genderLabel && (
          <p className="text-xs text-muted-foreground">{genderLabel}</p>
        )}
      </div>
      <span className="shrink-0 rounded-md bg-card px-2.5 py-1 text-xs font-bold text-foreground ring-1 ring-border">
        Seat {guest.seatNumber}
      </span>
    </li>
  );
}

function formatGuestGender(gender: string): string | null {
  const normalized = gender.trim().toUpperCase();
  if (normalized === "MALE") return "Male";
  if (normalized === "FEMALE") return "Female";
  if (normalized === "OTHER") return "Other";
  return gender.trim() || null;
}

function SummaryTile({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Clock;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-xl border border-border/70 p-3">
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </p>
      <p className="mt-1 text-sm font-bold text-foreground">{value}</p>
    </div>
  );
}

function Avatar({ name, src }: { name: string; src?: string }) {
  const initials = name
    .split(" ")
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className="h-11 w-11 rounded-full object-cover"
      />
    );
  }

  return (
    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-100 text-sm font-bold text-emerald-700">
      {initials}
    </span>
  );
}

function StoreButton({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-center rounded-lg bg-foreground px-4 py-2.5 text-sm font-semibold text-background transition-opacity hover:opacity-90"
    >
      {children}
    </a>
  );
}
